/**
 * Activity panel — right-side panel shown alongside the chat.
 *
 * Guest users: demo showcase cards (SHOWCASE_CARDS from landing.ts)
 * Authenticated users: real Composio activity logs from /api/activity/composio
 *
 * Same clean card UI for both — consistent experience.
 */

import { html, nothing, type TemplateResult } from "lit";
import { SHOWCASE_CARDS, APP_LOGOS, renderAppLogo, type ShowcaseCard } from "./landing.ts";

// ---------------------------------------------------------------------------
// Types (mirrors activity.ts ComposioActivityEntry)
// ---------------------------------------------------------------------------

interface ActivityEntry {
  id: string;
  toolName: string;
  toolkitSlug: string;
  appIcon: string | null;
  status: "success" | "error" | "running";
  durationMs: number | null;
  timestamp: string;
  summary: string;
  actionType: string;
  details: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Demo cards (for guests)
// ---------------------------------------------------------------------------

const DEMO_CARDS: ShowcaseCard[] = (() => {
  const seen = new Set<string>();
  const cards: ShowcaseCard[] = [];
  for (const card of SHOWCASE_CARDS) {
    if (!seen.has(card.app + card.actionType)) {
      seen.add(card.app + card.actionType);
      cards.push(card);
      if (cards.length >= 20) break;
    }
  }
  return cards;
})();

// ---------------------------------------------------------------------------
// Real activity state (for authenticated users)
// ---------------------------------------------------------------------------

let realEntries: ActivityEntry[] = [];
let realLoading = false;
let realError: string | null = null;
let realLoaded = false;
let _updateCb: (() => void) | null = null;

async function fetchActivity() {
  try {
    const { getClerk } = await import("../clerk-auth.ts");
    const clerk = await getClerk();
    if (!clerk.session) return;
    const token = await clerk.session.getToken();
    if (!token) return;
    const resp = await fetch("https://api.alitagpt.com/api/activity/composio?limit=20", {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (!resp.ok) throw new Error(`${resp.status}`);
    const data = await resp.json();
    realEntries = data.entries ?? [];
    realError = null;
  } catch {
    realError = "Couldn't load activity";
  }
}

export async function loadActivityForPanel(requestUpdate: () => void) {
  if (realLoaded) return;
  _updateCb = requestUpdate;
  realLoading = true;
  realLoaded = true;
  requestUpdate();
  await fetchActivity();
  realLoading = false;
  requestUpdate();
}

export function refreshActivityPanel() {
  realLoaded = false;
  if (_updateCb) loadActivityForPanel(_updateCb);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TOOLKIT_LABELS: Record<string, string> = {
  gmail: "Gmail", googlecalendar: "Google Calendar", googledocs: "Google Docs",
  googlesheets: "Google Sheets", googledrive: "Google Drive", slack: "Slack",
  github: "GitHub", notion: "Notion", linear: "Linear", hubspot: "HubSpot",
  telegram: "Telegram", discord: "Discord", twitter: "X (Twitter)", trello: "Trello",
  jira: "Jira", asana: "Asana", figma: "Figma", stripe: "Stripe", shopify: "Shopify",
  youtube: "YouTube", instagram: "Instagram", linkedin: "LinkedIn", zoom: "Zoom",
  dropbox: "Dropbox", outlook: "Outlook", calendly: "Calendly", salesforce: "Salesforce",
};

function toolkitLabel(slug: string): string {
  return TOOLKIT_LABELS[slug.toLowerCase()] || slug.charAt(0).toUpperCase() + slug.slice(1);
}

function relativeTime(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? "yesterday" : `${d}d ago`;
}

function activityAppLogo(slug: string, icon: string | null): TemplateResult {
  const s = slug.toLowerCase();
  if (APP_LOGOS[s]) return renderAppLogo(s, 22);
  if (icon) return html`<img src=${icon} alt=${s} width="22" height="22" style="border-radius:6px;object-fit:contain;flex-shrink:0;" loading="lazy" />`;
  return html`<div style="width:22px;height:22px;border-radius:6px;background:var(--bg-muted,#333);display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;">⚡</div>`;
}

function statusIcon(status: string): TemplateResult {
  if (status === "error") return html`<span style="color:#ef4444;flex-shrink:0;opacity:0.7;"><svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></span>`;
  if (status === "running") return html`<span class="demo-activity-running" style="flex-shrink:0;">●</span>`;
  return html`<span style="color:#16a34a;flex-shrink:0;opacity:0.7;"><svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M20 6L9 17l-5-5"/></svg></span>`;
}

// ---------------------------------------------------------------------------
// Render: individual cards
// ---------------------------------------------------------------------------

function renderDemoCard(card: ShowcaseCard): TemplateResult {
  return html`
    <div class="demo-activity-item">
      <div class="demo-activity-item-row">
        ${renderAppLogo(card.app, 22)}
        <div class="demo-activity-item-info">
          <div class="demo-activity-action">${card.actionType}</div>
          <div class="demo-activity-meta">${card.appLabel} · ${card.time}</div>
        </div>
        ${statusIcon("success")}
      </div>
      <div class="demo-activity-detail">${card.detail}</div>
    </div>
  `;
}

function renderRealCard(entry: ActivityEntry): TemplateResult {
  const label = toolkitLabel(entry.toolkitSlug);
  const detail = entry.summary || Object.values(entry.details || {}).join(" · ") || entry.toolName;
  return html`
    <div class="demo-activity-item">
      <div class="demo-activity-item-row">
        ${activityAppLogo(entry.toolkitSlug, entry.appIcon)}
        <div class="demo-activity-item-info">
          <div class="demo-activity-action">${entry.actionType || entry.toolName}</div>
          <div class="demo-activity-meta">${label} · ${relativeTime(entry.timestamp)}</div>
        </div>
        ${statusIcon(entry.status)}
      </div>
      <div class="demo-activity-detail">${detail}</div>
    </div>
  `;
}

function renderSkeleton(): TemplateResult {
  return html`${[1, 2, 3, 4, 5].map(() => html`
    <div class="demo-activity-item" style="opacity:0.4;">
      <div class="demo-activity-item-row">
        <div style="width:22px;height:22px;border-radius:6px;background:var(--border,#333);animation:act-pulse 1.5s infinite;"></div>
        <div class="demo-activity-item-info">
          <div style="height:12px;width:60%;border-radius:4px;background:var(--border,#333);animation:act-pulse 1.5s infinite;"></div>
          <div style="height:10px;width:40%;border-radius:4px;background:var(--border,#333);margin-top:4px;animation:act-pulse 1.5s infinite;"></div>
        </div>
      </div>
    </div>
  `)}`;
}

// ---------------------------------------------------------------------------
// Public render
// ---------------------------------------------------------------------------

/** For guests (unauthenticated) */
export function renderDemoActivityPanel(): TemplateResult | typeof nothing {
  return html`
    ${renderPanelShell(
      "Demo",
      html`${DEMO_CARDS.map(renderDemoCard)}`,
      html`
        <a href="https://accounts.alitagpt.com/sign-in" class="demo-activity-cta-btn">Get Started Free</a>
        <span class="demo-activity-cta-hint">7-day free trial · No credit card</span>
      `,
    )}
  `;
}

/** For authenticated users — real Composio activity */
export function renderLiveActivityPanel(): TemplateResult | typeof nothing {
  const content = realLoading
    ? renderSkeleton()
    : realError
      ? html`<div style="padding:24px 16px;text-align:center;font-size:12px;color:var(--text-muted,#9ca3af);">${realError}</div>`
      : realEntries.length === 0
        ? html`<div style="padding:32px 16px;text-align:center;">
            <div style="font-size:13px;color:var(--text-muted,#9ca3af);opacity:0.6;">No activity yet</div>
            <div style="font-size:11px;color:var(--text-muted,#9ca3af);opacity:0.4;margin-top:4px;">Actions will appear here as ALITA works</div>
          </div>`
        : html`${realEntries.map(renderRealCard)}`;

  return renderPanelShell(
    null,
    content,
    realEntries.length > 0
      ? html`<button class="demo-activity-cta-btn" style="background:var(--bg-muted,#333);color:var(--text,#fff);" @click=${refreshActivityPanel}>Refresh</button>`
      : nothing,
  );
}

// ---------------------------------------------------------------------------
// Shared panel shell
// ---------------------------------------------------------------------------

function renderPanelShell(
  badge: string | null,
  list: TemplateResult,
  footer: TemplateResult | typeof nothing,
): TemplateResult {
  return html`
    <div class="demo-activity-panel">
      <div class="demo-activity-header">
        <span class="demo-activity-title">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="opacity:0.6;">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
          </svg>
          History
        </span>
        ${badge ? html`<span class="demo-activity-badge">${badge}</span>` : nothing}
      </div>
      <div class="demo-activity-list">${list}</div>
      ${footer !== nothing ? html`<div class="demo-activity-cta">${footer}</div>` : nothing}
    </div>

    <style>
      .demo-activity-panel {
        position: fixed;
        top: var(--topbar-height, 48px);
        right: 0;
        bottom: 0;
        width: min(320px, 30vw);
        background: var(--card, #1a1a1a);
        border-left: 1px solid var(--border, rgba(255,255,255,0.08));
        display: flex;
        flex-direction: column;
        z-index: 10;
        overflow: hidden;
      }
      /* Push the chat content area left so it doesn't go under the panel */
      @media (min-width: 901px) {
        .content--chat {
          margin-right: min(320px, 30vw) !important;
        }
      }
      .demo-activity-header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 14px 16px;
        border-bottom: 1px solid var(--border, rgba(255,255,255,0.08));
        flex-shrink: 0;
      }
      .demo-activity-title {
        display: flex; align-items: center; gap: 6px;
        font-size: 13px; font-weight: 600; color: var(--text, #fff);
      }
      .demo-activity-badge {
        font-size: 10px; font-weight: 600; color: var(--accent, #D97757);
        background: rgba(217, 119, 87, 0.12);
        padding: 2px 8px; border-radius: 10px;
        text-transform: uppercase; letter-spacing: 0.5px;
      }
      .demo-activity-list { flex: 1; overflow-y: auto; padding: 8px 0; }
      .demo-activity-item {
        padding: 10px 16px;
        border-bottom: 1px solid var(--border, rgba(255,255,255,0.04));
        transition: background 0.15s;
      }
      .demo-activity-item:hover { background: var(--hover, rgba(255,255,255,0.03)); }
      .demo-activity-item-row { display: flex; align-items: center; gap: 10px; margin-bottom: 4px; }
      .demo-activity-item-info { flex: 1; min-width: 0; }
      .demo-activity-action {
        font-size: 12px; font-weight: 600; color: var(--text, #fff);
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .demo-activity-meta { font-size: 10px; color: var(--text-muted, #9ca3af); }
      .demo-activity-detail {
        font-size: 11px; color: var(--text-muted, #9ca3af); line-height: 1.4;
        overflow: hidden; display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical;
      }
      .demo-activity-cta {
        padding: 16px;
        border-top: 1px solid var(--border, rgba(255,255,255,0.08));
        text-align: center; flex-shrink: 0;
      }
      .demo-activity-cta-btn {
        display: block; width: 100%; padding: 10px 16px;
        background: var(--accent, #D97757); color: #fff;
        font-size: 13px; font-weight: 600; border: none; border-radius: 8px;
        cursor: pointer; text-decoration: none; text-align: center; transition: opacity 0.15s;
      }
      .demo-activity-cta-btn:hover { opacity: 0.9; }
      .demo-activity-cta-hint { display: block; font-size: 11px; color: var(--text-muted, #9ca3af); margin-top: 6px; }
      .demo-activity-running { color: #f59e0b; animation: act-pulse 1.5s infinite; font-size: 10px; }
      @keyframes act-pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
      @media (max-width: 900px) {
        .demo-activity-panel { display: none !important; }
        .content--chat { margin-right: 0 !important; }
      }

      /* Prevent any horizontal overflow on mobile */
      .shell, .content, .content--chat {
        max-width: 100vw;
        overflow-x: hidden;
      }
    </style>
  `;
}
